/**
 * Admin Data Store
 * Jotai atoms for admin data (superusers, users, organization settings)
 * Uses jotai-tanstack-query for server state management with caching
 */

import { atomWithQuery } from "jotai-tanstack-query";
import { adminAPI } from "@/services";
import type {
  ISuperuser,
  IUser,
  IOrganizationSettings,
  IOrganization,
  IEndpointGroup,
  IOrgEndpointGroups,
  IPage,
  IOrgPages,
} from "@/services";

/**
 * Superusers query atom
 * - Fetches all superusers from API
 * - 2 minute stale time (may change moderately)
 */
export const superusersQueryAtom = atomWithQuery(() => ({
  queryKey: ["superusers"],
  queryFn: () => adminAPI.getSuperusers(),
  staleTime: 2 * 60 * 1000, // 2 minutes
}));

/**
 * Users query atom
 * - Fetches all users for autocomplete
 * - 5 minute stale time (rarely changes)
 */
export const usersQueryAtom = atomWithQuery(() => ({
  queryKey: ["users"],
  queryFn: () => adminAPI.getUsers(),
  staleTime: 5 * 60 * 1000, // 5 minutes
}));

/**
 * Organization settings query atom
 * - Fetches organization settings
 * - 5 minute stale time
 */
export const organizationSettingsQueryAtom = atomWithQuery(() => ({
  queryKey: ["organizationSettings"],
  queryFn: () => adminAPI.getOrganizationSettings(),
  staleTime: 5 * 60 * 1000, // 5 minutes
}));

// ============ SUPERADMIN ATOMS ============

/**
 * Admin users query atom (emails with admin role)
 * - Fetches all admin user emails
 * - 2 minute stale time
 */
export const adminsQueryAtom = atomWithQuery(() => ({
  queryKey: ["admins"],
  queryFn: () => adminAPI.getAdmins(),
  staleTime: 2 * 60 * 1000, // 2 minutes
}));

/**
 * All users query atom (for superadmin autocomplete)
 * - Fetches all users across all organizations
 * - 5 minute stale time
 */
export const allUsersQueryAtom = atomWithQuery(() => ({
  queryKey: ["allUsers"],
  queryFn: () => adminAPI.getAllUsers(),
  staleTime: 5 * 60 * 1000, // 5 minutes
}));

/**
 * Organizations query atom
 * - Fetches all organizations
 * - 2 minute stale time (may change with activation/deactivation)
 */
export const organizationsQueryAtom = atomWithQuery(() => ({
  queryKey: ["organizations"],
  queryFn: () => adminAPI.getOrganizations(),
  staleTime: 2 * 60 * 1000, // 2 minutes
}));

/**
 * Endpoint groups query atom
 * - Fetches all available endpoint groups
 * - 10 minute stale time (rarely changes)
 */
export const endpointGroupsQueryAtom = atomWithQuery(() => ({
  queryKey: ["endpointGroups"],
  queryFn: () => adminAPI.getEndpointGroups(),
  staleTime: 10 * 60 * 1000, // 10 minutes
}));

/**
 * Organization endpoint group access query atom
 * - Fetches organization-to-endpoint-group mappings
 * - 2 minute stale time
 */
export const orgEndpointGroupsQueryAtom = atomWithQuery(() => ({
  queryKey: ["orgEndpointGroups"],
  queryFn: () => adminAPI.getOrgEndpointGroups(),
  staleTime: 2 * 60 * 1000, // 2 minutes
}));

/**
 * Pages query atom
 * - Fetches all available pages
 * - 10 minute stale time (rarely changes)
 */
export const pagesQueryAtom = atomWithQuery(() => ({
  queryKey: ["pages"],
  queryFn: () => adminAPI.getPages(),
  staleTime: 10 * 60 * 1000, // 10 minutes
}));

/**
 * Organization page access query atom
 * - Fetches organization-to-page mappings
 * - 2 minute stale time
 */
export const orgPagesQueryAtom = atomWithQuery(() => ({
  queryKey: ["orgPages"],
  queryFn: () => adminAPI.getOrgPages(),
  staleTime: 2 * 60 * 1000, // 2 minutes
}));

// Re-export types for convenience
export type {
  ISuperuser,
  IUser,
  IOrganizationSettings,
  IOrganization,
  IEndpointGroup,
  IOrgEndpointGroups,
  IPage,
  IOrgPages,
};
